"use client";

import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react";
import { Trans } from "@lingui/react/macro";
import { useMutation, useQuery } from "@urql/next";
import { graphql } from "gql.tada";
import { ArrowLeftIcon, Loader2Icon, PencilIcon } from "lucide-react";
import dynamic from "next/dynamic";
import { type ReactNode, useCallback, useRef, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAiChatUiContext } from "@/lib/ai/ai-chat-ui-context";
import { UpdateAgentChat } from "@/lib/ai/mutation";
import { ChatList } from "../chat-list/chat-list";
import { MobileChatListButton } from "../chat-list/mobile-chat-list-button";
import { useChatHistoryToggleButton } from "./chat-history-toggle-context";

const ChatMenu = dynamic(
  () => import("../chat-menu").then((m) => ({ default: m.ChatMenu })),
  { ssr: false },
);

type Props = {
  chatId?: string;
  title?: string;
  shared?: boolean;
  isCreatedByViewer?: boolean;
  actions?: ReactNode;
};

/**
 * チャットスレッドのヘッダーを描画する。
 *
 * @remarks
 * plugin 固有 UI は `actions` slot から差し込み、共通ヘッダー自身は
 * 戻る・タイトル編集・メニューなど chat 共通機能だけを扱う。
 */
export function ChatThreadHeader({
  chatId,
  title,
  shared: _sharedProp,
  isCreatedByViewer,
  actions,
}: Props) {
  const { i18n } = useLingui();
  const historyToggleButton = useChatHistoryToggleButton();
  const { clearChatSelection } = useAiChatUiContext();
  const [{ fetching }, updateChat] = useMutation(UpdateAgentChat);
  const [isEditing, setIsEditing] = useState(false);
  const [draftTitle, setDraftTitle] = useState(title ?? "");
  const inputRef = useRef<HTMLInputElement | null>(null);
  const hasCancelledRenameRef = useRef(false);

  // タイトルはサーバー初期値に加え、クライアントで購読して反映
  const ChatHeaderQuery = graphql(`
    query ChatHeader($id: ID!) {
      node(id: $id) {
        __typename
        ... on AgentChat { id title }
      }
    }
  `);
  const [{ data: headerData }] = useQuery({
    query: ChatHeaderQuery,
    variables: { id: chatId ?? "" },
    pause: !chatId,
  });
  const liveTitle =
    headerData?.node && headerData.node.__typename === "AgentChat"
      ? (headerData.node.title ?? undefined)
      : undefined;

  const handleBack = useCallback(() => {
    clearChatSelection();
  }, [clearChatSelection]);

  const focusInput = () => {
    setTimeout(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    }, 0);
  };

  const startRename = () => {
    if (!isCreatedByViewer || !chatId) return;
    hasCancelledRenameRef.current = false;
    setDraftTitle(liveTitle ?? title ?? "");
    setIsEditing(true);
    focusInput();
  };

  const handleRenameKeydown = (
    event: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (event.key === "Enter" && !event.nativeEvent.isComposing) {
      event.preventDefault();
      handleRenameSubmit();
    }
    if (event.key === "Escape") {
      event.preventDefault();
      handleRenameCancel();
    }
  };

  const handleRenameCancel = () => {
    hasCancelledRenameRef.current = true;
    setIsEditing(false);
    setDraftTitle(liveTitle ?? title ?? "");
  };

  const handleRenameBlur = () => {
    if (hasCancelledRenameRef.current) return;
    handleRenameSubmit();
  };

  const handleRenameSubmit = async () => {
    if (!chatId) return;
    if (fetching) return;
    const result = await updateChat({
      input: { id: chatId, title: draftTitle },
    });
    if (result.error) {
      toast.error(i18n._(msg`Failed to rename chat`), {
        description: result.error.message,
      });
      return;
    }
    setIsEditing(false);
  };
  return (
    <div className="flex h-11 shrink-0 items-center justify-between border-b px-2">
      <div className="flex items-center gap-2 min-w-0 flex-1">
        {historyToggleButton}
        {chatId ? (
          <Button
            onClick={handleBack}
            size="icon-sm"
            variant="ghost"
            aria-label="back-to-new"
          >
            <ArrowLeftIcon className="h-4 w-4" />
          </Button>
        ) : null}
        {chatId ? (
          isEditing ? (
            <div className="flex items-center gap-2 min-w-0 flex-1">
              <Input
                ref={(input) => {
                  inputRef.current = input;
                }}
                value={draftTitle}
                onChange={(e) => setDraftTitle(e.target.value)}
                onKeyDown={handleRenameKeydown}
                onBlur={handleRenameBlur}
                placeholder={i18n._(msg`Enter chat title`)}
                className="flex-1 border-0 bg-transparent px-0 py-0 h-7 font-bold shadow-none focus-visible:ring-0 focus-visible:ring-offset-0"
              />
              {fetching ? (
                <Loader2Icon className="h-4 w-4 animate-spin text-muted-foreground" />
              ) : null}
            </div>
          ) : (
            <div className="group flex min-w-0 flex-1 items-center gap-1">
              <h2 className="truncate text-sm font-medium leading-7">
                {liveTitle ?? title ?? <Trans>(No title)</Trans>}
              </h2>
              {isCreatedByViewer ? (
                <Button
                  type="button"
                  size="icon-sm"
                  variant="ghost"
                  className="ml-0.5 opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100"
                  aria-label={i18n._(msg`Rename chat`)}
                  onClick={startRename}
                >
                  <PencilIcon className="h-4 w-4 text-muted-foreground" />
                </Button>
              ) : null}
            </div>
          )
        ) : null}
      </div>
      <div className="flex items-center gap-1">
        {actions}
        <MobileChatListButton list={<ChatList headerClassName="pr-8" />} />
        {chatId && !isEditing ? (
          <ChatMenu
            chatId={chatId}
            onRenameRequest={(_title) => startRename()}
            onDeleted={() => {
              clearChatSelection();
            }}
          />
        ) : null}
      </div>
    </div>
  );
}
